import React, { useEffect, useContext } from "react";
import { useImmer } from "use-immer";
import { successMessage, errorMessage } from "../../../utils";
import { getWebOrderFeeSettings, updateWebOrderFeeSettings } from "../api/SettingsApi";
import { getEmailSettingsDetails, updateEmailSettings } from "../../EmailSettings";
import SimpleReactValidator from "simple-react-validator";
import moment from "moment";

// Add custom validation rule
SimpleReactValidator.addLocale('en', {
  future_date: 'The :attribute must be a future date.'
});

const customValidationRules = {
  future_date: {
    message: 'The :attribute must be a future date.',
    rule: (val) => {
      if (!val) return false;
      const selectedDate = moment(val);
      const tomorrow = moment().add(1, 'day').startOf('day');
      return selectedDate.isSameOrAfter(tomorrow);
    }
  }
};

export const useGenaralSettings = () => {
    const [, forceUpdate] = useImmer(0);
    const formValidator = React.useRef(
        new SimpleReactValidator({
            validators: customValidationRules,
            autoForceUpdate: { forceUpdate: () => forceUpdate(Math.random()) },
            element: (message) => message,
        })
    );

    const [state, setState] = useImmer({
        isLoading: true,
        plBsDetails: "",
        isSaveButtonBusy: false,
        hasPlBsChanges: false, // Add this flag
        // Web Order Fee settings
        webOrderFeeSettings: {
            fee_status: false,
            fee_type: "fixed",
            fee_value: 0,
            fee_apply_date: "",
            fee_apply_date_type: "fixed",
            fee_apply_date_value: "",
        },
        isWebOrderFeeBusy: false,
        hasWebOrderFeeChanges: false
    });

    useEffect(() => {
        handleGetPlBsDetails();
        handleGetWebOrderFeeSettings();
    }, []);

    const handleGetPlBsDetails = async () => {
        console.log("Fetching PL and BS details...");

        try {
            const res = await getEmailSettingsDetails("pl-and-bs-file");
            if (res.success) {
                console.log(res.data.emails[0].email);

                setState((draft) => {
                    draft.plBsDetails = res.data.emails[0].email;
                    draft.isLoading = false;
                });
            } else {
                errorMessage(res.message);
            }
        } catch (err) {
            setState((draft) => {
                draft.isLoading = false;
            });
        }
    };

    const handleGetWebOrderFeeSettings = async () => {
        try {
            const res = await getWebOrderFeeSettings();
            if (res.success) {
                setState((draft) => {
                    draft.webOrderFeeSettings = res.data
                });
            } else {
                errorMessage(res.message);
            }
        } catch (err) {
            console.error("Error fetching web order fee settings:", err);
        }
    };

    const onSaveEmailSettings = async () => {
        if (formValidator.current.allValid()) {
            const payload = { emails: state.plBsDetails }
            setState((draft) => {
                draft.isSaveButtonBusy = true;
            });
            try {
                const response = await updateEmailSettings(15, payload);
                const { success, message } = response;
                if (success) {
                    successMessage(message);
                    setState((draft) => {
                        draft.hasPlBsChanges = false; // Reset changes flag
                    });
                } else {
                    errorMessage(message);
                }
            } catch (err) {
                errorMessage(err.response.data.message);
            } finally {
                setState((draft) => {
                    draft.isSaveButtonBusy = false;
                });
            }
        } else {
            formValidator.current.showMessages();
        }
    };

    const onSaveWebOrderFeeSettings = async () => {
        if (formValidator.current.allValid()) {
            let payload = {};
            if (state.webOrderFeeSettings.fee_status) {
              payload = {
                fee_status: state.webOrderFeeSettings.fee_status,
                fee_apply_date_type:
                  state.webOrderFeeSettings.fee_apply_date_type,
                fee_apply_date_value: parseFloat(
                  state.webOrderFeeSettings.fee_apply_date_value,
                ),
                fee_apply_date: moment(
                  state.webOrderFeeSettings.fee_apply_date,
                ).format("YYYY-MM-DD"),
              };
            } else {
              payload = {
                fee_status: false,
              };
            }
            

            setState((draft) => {
                draft.isWebOrderFeeBusy = true;
            });

            try {
                const response = await updateWebOrderFeeSettings(payload);
                const { success, message } = response;
                if (success) {
                    successMessage(message);
                    setState((draft) => {
                        draft.hasWebOrderFeeChanges = false; // Reset changes flag
                    });
                } else {
                    errorMessage(message);
                }
            } catch (err) {
                errorMessage(err.response?.data?.message || "An error occurred");
            } finally {
                setState((draft) => {
                    draft.isWebOrderFeeBusy = false;
                });
            }
        } else {
            formValidator.current.showMessages();
            forceUpdate(1);
        }
    };

    const handleDayChange = (e) => {
        setState((draft) => {
            draft.plBsDetails = e.target.value;
            draft.hasPlBsChanges = true; // Mark as changed
        });
    };

    const handleWebOrderFeeChange = (field, value) => {
        console.log(`Updating web order fee setting: ${field} = ${value}`);

        setState((draft) => {
            draft.webOrderFeeSettings[field] = value;
            draft.hasWebOrderFeeChanges = true; // Mark as changed
        });
    };

    return {
        state,
        onSaveEmailSettings,
        handleDayChange,
        handleWebOrderFeeChange,
        onSaveWebOrderFeeSettings,
        formValidator
    };
};
